/*
 * Confidential and Proprietary.
 * Do not distribute without 1-800-Flowers.com, Inc. consent.
 * Copyright 1-800-Flowers.com, Inc. 2019. All rights reserved.
 */

import React, { useEffect } from 'react';
import {
    array, bool, func, number, object, string,
} from 'prop-types';
import { useQuery } from '@apollo/client';
import mbpLogger from '@module-logger/mbp';
import makeStyles from '@material-ui/core/styles/makeStyles';
import { Link } from 'react-router-dom';
import ReactHtmlParser from 'react-html-parser';
import { useDispatch, useSelector } from 'react-redux';

import getAutoSuggest from '../../../../gql/queries/getAutoSuggest';
import { trackEvent } from '../../../../../state/ducks/TagManager/ducks/TagManager/TagManager-Actions';
import { emitCustomTrackingEvent } from '../../../../../state/ducks/TagManager/ducks/ClickStreamEvents/ClickStreamEvent-Actions';
import useExperimentServiceTracking from '../../../../helpers/experimentService/useExperimentServiceTracking';
import { setVertexAITrackingData } from '../../../../../state/ducks/App/App-Actions';
import { getCurrentPageType } from '../../../../../state/ducks/TagManager/ducks/TagManager/TagManager-Selectors';
import useRecentlySearched from '../../../../helpers/useRecentlySearched';

const useStyles = makeStyles((theme) => ({
    desktopRoot: (props) => ({
        position: 'absolute',
        zIndex: '25',
        background: '#fff',
        top: '46px',
        left: 0,
        width: props.inputWidth ? `${props.inputWidth}px` : '100%',
        borderBottomRightRadius: '5px',
        borderBottomLeftRadius: '5px',
        paddingBottom: '6px',
        boxShadow: '0px 4px 4px 0px #00000040',
        maxHeight: 'calc(100vh - 92px)',
        overflowY: 'auto',
    }),
    phraseSuggestions: {
        listStyle: 'none',
        padding: '0px',
        margin: '0px',
        '& a': {
            display: 'block',
            padding: '16px',
            textDecoration: 'none',
            color: '#000000',
            fontSize: '14px',
            letterSpacing: '0.5px',
            lineHeight: '19px',
            fontWeight: '800',
            '&:hover': {
                backgroundColor: '#F5F5F5',
                borderRadius: '3px',
            },
            '& span': {
                fontWeight: 'normal',
            },
        },
    },
    selectedSuggestion: {
        backgroundColor: '#F5F5F5',
        borderRadius: '6px',
    },
    noResults: {
        padding: '16px 0',
        fontSize: '14px',
        color: '#555555',
        textAlign: 'center',
    },
    border: {
        margin: '0px 16px',
        border: 'none',
        borderBottom: '1px solid #E9E9E9',
    },
    searchMenuTitle: {
        fontSize: '14px',
        fontWeight: 700,
        marginBottom: '12px',
    },
}));

/**
 *
 * @param {*} str take the input from search box
 * @return string
 * @example input val test"s it will return test\"s
 *  @example input val \ it will return ' '
 */
const escapeDoubleQuotes = (str) => str.replace(/"/g, '\\"');

const suggestionListItems = (
    classes,
    phrases,
    handleSuggestionClick,
    inputVal,
    concatSearchTerm,
    cleanTerm,
    selectedSuggestionIndex,
    suggestionsDataRef,
    isDesktop,
) => {
    const suggestionList = [];
    let response = <div className={classes.noResults} role="status" aria-live="polite">No Results found</div>;
    if (phrases?.length) {
        phrases.slice(0, 5).forEach((phrase) => {
            if (!phrase) return;
            const data = {};
            data.name = phrase.toLowerCase().replace(/\+/g, ' ').replace(inputVal, `<span>${inputVal}</span>`);
            data.inputString = phrase.toLowerCase();
            data.link = concatSearchTerm(cleanTerm(phrase.toLowerCase())?.replace(/\s+/g, '+'), phrase); // replacing spaces with +
            suggestionList.push(data);
        });
    }

    // Update the ref with current suggestions data
    if (suggestionsDataRef) {
        // eslint-disable-next-line no-param-reassign
        suggestionsDataRef.current = suggestionList;
    }

    if (suggestionList.length) {
        response = suggestionList.map((data, index) => (
            <React.Fragment key={data.link}>
                <Link
                    id={`suggestion-${index}`}
                    className={selectedSuggestionIndex === index ? classes.selectedSuggestion : ''}
                    aria-selected={selectedSuggestionIndex === index ? 'true' : 'false'}
                    role="option"
                    aria-label={`Search suggestion ${index + 1} of ${suggestionList.length}: ${data.inputString}`}
                    tabIndex="-1"
                    to={`/searchterm/${data.link}`}
                    onClick={() => handleSuggestionClick(data, index + 1)}
                >
                    {ReactHtmlParser(data.name)}
                </Link>
                {!(isDesktop && index === suggestionList.length - 1) && <hr className={classes.border} />}
            </React.Fragment>
        ));
    }
    return response;
};

const SearchSuggestions = ({
    isDesktop,
    phrase,
    brand,
    targetingEnabled,
    experimentService,
    targeting,
    setSearchValue,
    cleanTerm,
    concatSearchTerm,
    inputWidth,
    setShowSuggestions,
    selectedSuggestionIndex,
    suggestionsDataRef,
    handleMobileClose,
    trackExperiment,
}) => {
    const classes = useStyles({ inputWidth: isDesktop ? inputWidth : undefined });
    const dispatch = useDispatch();
    const pageType = useSelector(getCurrentPageType);

    const { addRecentlySearched } = useRecentlySearched();

    const { trackGA4Experiments, trackClickStreamExperiments } = useExperimentServiceTracking();

    const searchPhrase = phrase.replace(/\\/g, ' ');

    const FIND_SUGGESTIONS = getAutoSuggest({
        targetingEnabled,
        brand,
        phrase: encodeURIComponent(escapeDoubleQuotes(searchPhrase)),
        experimentsService: experimentService,
    });
    const { error, data } = useQuery(FIND_SUGGESTIONS, {
        variables: { ...(targetingEnabled ? { targeting } : {}), phrase },
        context: {
            api: experimentService ? 'experiments' : 'aggregator',
        },
    });

    // tracking impression event for experiment service
    useEffect(() => {
        if (data?.autoSuggest?.experiments?.length && typeof window !== 'undefined' && !window.wuAutoSuggestImpressionFired) {
            trackGA4Experiments({ experiments: data.autoSuggest.experiments, eventAction: 'Search Test' });
            trackClickStreamExperiments({
                experiments: data.autoSuggest.experiments,
                action: 'Impression',
                page: { type: pageType },
            });
            window.wuAutoSuggestImpressionFired = true;
        }
    }, [data?.autoSuggest?.experiments]);

    // tracking impression for autocomplete feature
    useEffect(() => {
        if (data?.autoSuggest?.phrases?.length > 0) {
            dispatch(emitCustomTrackingEvent({
                eventType: 'custom',
                eventSubType: 'feature-tracking',
                action: 'Impression',
                category: 'Search',
                label: 'autocomplete',
            }));
        }
        // tracking input event for for user queried phrases once
        dispatch(trackEvent({
            eventCategory: 'Search',
            eventAction: 'search_input',
            eventLabel: 'SEARCH INTERACTION | <<pageType>>',
            searchVariety: 'search_input',
            searchPhrase,
            eventName: 'search interaction',
        }));
    }, [data?.autoSuggest?.phrases]);

    // Scroll selected suggestion into view
    useEffect(() => {
        if (selectedSuggestionIndex >= 0) {
            const element = document.getElementById(`suggestion-${selectedSuggestionIndex}`);
            if (element) {
                element.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
            }
        }
    }, [selectedSuggestionIndex]);

    if (error) {
        mbpLogger.logError({
            appName: process.env.npm_package_name,
            jsError: error,
            message: `Failed to fetch content data for: auto suggestions from SearchSuggestions on brand ${brand}`,
        });
    }

    if (!data?.autoSuggest) {
        return <></>;
    }

    const handleSuggestionClick = (phraseData, selectedPosition) => {
        setSearchValue(phraseData.inputString);
        dispatch(trackEvent({
            eventCategory: 'Search',
            eventAction: 'search_suggest_click',
            eventLabel: 'SEARCH INTERACTION | <<pageType>>',
            searchVariety: 'search_suggest_click',
            searchPhrase: phrase.toLowerCase(),
            eventName: 'search interaction',

            search_type: 'search_suggest_click',
            search_term: phrase.toLowerCase(),
            unique_search_term: phrase.toLowerCase(),
        }));
        if (data?.autoSuggest?.attributionToken) {
            dispatch(setVertexAITrackingData({ completionAttributionToken: data?.autoSuggest?.attributionToken, selectedPosition }));
        }
        addRecentlySearched({ searchTerm: phraseData.inputString, value: cleanTerm(phraseData.inputString) });
        setShowSuggestions(false);
        handleMobileClose();
        dispatch(emitCustomTrackingEvent({
            eventType: 'custom',
            eventSubType: 'feature-tracking',
            action: 'Click',
            category: 'Search',
            label: 'autocomplete',
        }));
        trackExperiment('suggestion', 'autocomplete');
    };

    return (
        <div
            className={isDesktop ? `${classes.desktopRoot}` : ''}
        >
            {data?.autoSuggest?.phrases?.length >= 0 && (
                <ul
                    className={classes.phraseSuggestions}
                    id="suggestion"
                    role="listbox"
                    aria-label="Search suggestions list"
                >
                    {suggestionListItems(
                        classes,
                        data.autoSuggest.phrases,
                        handleSuggestionClick,
                        phrase.toLowerCase(),
                        concatSearchTerm,
                        cleanTerm,
                        selectedSuggestionIndex,
                        suggestionsDataRef,
                        isDesktop,
                    )}
                </ul>
            )}
        </div>
    );
};

SearchSuggestions.propTypes = {
    phrase: string,
    setSearchValue: func,
    brand: string.isRequired,
    experimentService: bool,
    targetingEnabled: bool,
    targeting: array,
    concatSearchTerm: func,
    cleanTerm: func,
    isDesktop: bool,
    inputWidth: number,
    setShowSuggestions: func,
    selectedSuggestionIndex: number,
    suggestionsDataRef: object,
    handleMobileClose: func,
    trackExperiment: func,
};

SearchSuggestions.defaultProps = {
    phrase: '',
    setSearchValue: () => {},
    experimentService: false,
    targetingEnabled: false,
    targeting: [],
    concatSearchTerm: () => {},
    cleanTerm: () => {},
    isDesktop: true,
    inputWidth: undefined,
    setShowSuggestions: () => {},
    selectedSuggestionIndex: -1,
    suggestionsDataRef: null,
    handleMobileClose: () => {},
    trackExperiment: () => {},
};

export default SearchSuggestions;
